import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from 'antd';

import ItemTagChips from './ItemTagChips';
import ItemTagPicker from './ItemTagPicker';
import useCollectionTags from '../../hooks/useCollectionTags';
import { rememberItemTagTitles, rememberedItemTagTitles } from '../../hooks/useTagSearch';
import { useOptionalTags } from '../../contexts/TagsContext';
import { normalizeId } from '../../utils/ids';
import { applyTagDelta, autoTagIdsOf, canRemoveEntry, handTagIdsOf, itemTagEntries, withPrivateDelta } from '../../utils/itemTags';

/**
 * THE ITEM FORM'S TAG ROW (docs/plans/tags.md §5.2): the chips and "+ Add
 * tag" at the foot of the form's last panel. Unlike the read view, nothing
 * here is saved on its own — the tags are part of the form and go with Save:
 *
 * - hand-applied tags as the item's `tagIds`, in the create or update body
 *   (so they work on a NEW item: they ride the create);
 * - private tags as the form's `myTags`, applied through the person's own
 *   store once the save has landed and the item has an id (ItemForm).
 *
 * `onChange(patch)` hands back `{ tagIds }`, `{ myTags }` and — for an item
 * tag picked by title — `{ tagTitles }`, which the form keeps beside the item
 * (none of them is sent where the API would read it wrong: ItemForm strips
 * `myTags` and `tagTitles` at the wire). A read-only form shows the chips
 * with no ✕ and no picker.
 *
 * `note` (T9): what a quick add, a duplicate or a draft LEFT OFF — the tags
 * it carried that this person can't put on here — said once, beside the tags
 * it did put on (utils/itemTags.js launchTagsNote). A template never applies
 * a tag the person can't see land: what lands is the chips, before Save.
 *
 * `activeTagId` (a NEW item only — ItemForm never passes it on an edit): the
 * person's ACTIVE TAG, which the form put on as a private tag; its chip says
 * "Your active tag" and its ✕ leaves it off this item alone
 * (docs/plans/tags-active-tag-proposal.md).
 */
export default function ItemFormTags({
	item,
	collection = null,
	canEdit = false,
	onChange,
	pickerOpen,
	onPickerOpenChange,
	note = null,
	onDismissNote,
	activeTagId = null,
}) {
	const itemCollectionId = normalizeId(item?.collection_id) || normalizeId(collection?._id) || null;
	// Asked for when the item carries collection tags or the picker opens —
	// a form with no tags asks nothing.
	const [pickerOpenState, setPickerOpenState] = useState(false);
	const pickerIsOpen = typeof pickerOpen === 'boolean' ? pickerOpen : pickerOpenState;
	const wantsVocabulary = pickerIsOpen || handTagIdsOf(item).length > 0 || autoTagIdsOf(item).length > 0;
	const { tags: vocabulary } = useCollectionTags(itemCollectionId, { enabled: wantsVocabulary });
	const privateTags = useOptionalTags()?.tags || null;
	useEffect(() => { rememberItemTagTitles(item); }, [item]);

	const entries = useMemo(
		() => itemTagEntries(item, { vocabulary, privateTags, titles: rememberedItemTagTitles(), activeTagId }),
		[item, vocabulary, privateTags, activeTagId]
	);

	const patch = next => { if (typeof onChange === 'function') onChange(next); };
	const withTitles = titles => (titles ? { tagTitles: { ...(item?.tagTitles || {}), ...titles } } : {});

	const handleRemove = entry => {
		if (entry.private) patch({ myTags: withPrivateDelta(item?.myTags, { remove: [{ tagId: entry.id }] }) });
		else patch({ tagIds: applyTagDelta(handTagIdsOf(item), { remove: [entry.id] }) });
	};

	const handleToggle = ({ kind, id, on, name, titles, itemTag }) => {
		if (kind === 'private') {
			const application = { tagId: id, kind: itemTag ? 'item' : 'tag', ...(name ? { name } : {}) };
			patch({
				myTags: withPrivateDelta(item?.myTags, on ? { add: [application] } : { remove: [application] }),
				...withTitles(titles),
			});
			return;
		}
		patch({
			tagIds: applyTagDelta(handTagIdsOf(item), on ? { add: [id] } : { remove: [id] }),
			...withTitles(titles),
		});
	};

	return (
		<div className="form-panel__tags" data-testid="item-form-tags">
			<span className="form-panel__tags-label" id="item-form-tags-label">Tags</span>
			{note ? (
				<Alert
					type="info"
					showIcon
					closable
					className="form-panel__tags-note"
					message={note}
					onClose={onDismissNote}
					data-testid="item-form-tags-note"
				/>
			) : null}
			<ItemTagChips
				entries={entries}
				onRemove={canEdit ? handleRemove : undefined}
				canRemove={entry => canRemoveEntry(entry, { canEdit, collection })}
			/>
			{canEdit ? (
				<ItemTagPicker
					item={item}
					collection={collection}
					canEdit={canEdit}
					vocabulary={vocabulary}
					privateTags={privateTags}
					onToggle={handleToggle}
					open={pickerIsOpen}
					onOpenChange={next => { setPickerOpenState(next); onPickerOpenChange?.(next); }}
				/>
			) : null}
		</div>
	);
}
